import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Group, Role } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { GroupDetail } from "@/modules/settings/screens/GroupDetail";
import { NewGroupSheet } from "@/modules/settings/screens/NewGroupSheet";
import { projectGroup, type Projection } from "@/modules/settings/projections";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * The Groups list (M8-9, FR-ACL-6, M15-3): each group, the roles it grants,
 * and now — since `SettingsRepository` grew `createGroup`, `renameGroup`,
 * `deleteGroup`, `setGroupMembers` and `setGroupRoles` — the write UI to
 * manage them. "New group" opens `NewGroupSheet`; a row opens `GroupDetail`
 * in the same `RecordDrawer` `UsersList` uses for a user, for the same
 * reason: `SettingsRepository` has no `getGroup(id)`, and this ERP's group
 * list is small enough that the row a click came from is always on the page
 * that is showing it.
 */
export function GroupsList({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const { recordId, open } = useOpenRecord();
  const [creating, setCreating] = useState(false);

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const groupsQuery = useQuery({
    queryKey: ["settings", "groups", filter, sort, limit, cursor],
    queryFn: () => data.settings.listGroups({ filter, sort, limit, cursor }),
  });

  const rolesQuery = useQuery({
    queryKey: ["settings", "roles", "names"],
    queryFn: () => data.settings.listRoles(),
  });

  // Member count is a fact about `User.groupIds`, not about `Group` itself
  // (see `GROUP_FIELDS.memberCount`), so it takes the whole user list to
  // answer — every user in this fixture, not one page of them, since a
  // group's true membership does not stop at whatever page of Users someone
  // last had open. `GroupDetail`'s membership editor reads the same list.
  const usersQuery = useQuery({
    queryKey: ["settings", "users", "all", "for-member-count"],
    queryFn: () => data.settings.listUsers({ limit: 200 }),
  });

  if (groupsQuery.isPending || rolesQuery.isPending || usersQuery.isPending) {
    return <TableSkeleton />;
  }

  if (groupsQuery.isError) {
    return (
      <LoadFailed
        message={groupsQuery.error.message}
        onRetry={() => {
          void groupsQuery.refetch();
        }}
      />
    );
  }
  if (rolesQuery.isError) {
    return (
      <LoadFailed
        message={rolesQuery.error.message}
        onRetry={() => {
          void rolesQuery.refetch();
        }}
      />
    );
  }
  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }

  const roleNames = new Map<string, string>(
    rolesQuery.data.items.map((role: Role) => [role.id, role.name]),
  );

  const memberCounts = new Map<string, number>();
  for (const user of usersQuery.data.items) {
    for (const groupId of user.groupIds) {
      memberCounts.set(groupId, (memberCounts.get(groupId) ?? 0) + 1);
    }
  }

  function project(group: Group): Projection {
    return {
      ...projectGroup(group),
      memberCount: memberCounts.get(group.id) ?? 0,
    };
  }

  const selectedGroup = groupsQuery.data.items.find(
    (group) => group.id === recordId,
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus />
          New group
        </Button>
      </div>
      <Fab
        label="New group"
        icon={<Plus aria-hidden />}
        onClick={() => {
          setCreating(true);
        }}
      />

      {selectedGroup ? (
        <RecordDrawer
          title={selectedGroup.name}
          description={selectedGroup.description ?? undefined}
        >
          <GroupDetail
            group={selectedGroup}
            users={usersQuery.data.items}
            roles={rolesQuery.data.items}
          />
        </RecordDrawer>
      ) : null}

      <NewGroupSheet
        open={creating}
        onOpenChange={setCreating}
        onCreated={open}
      />

      <DataTable
        fields={fields}
        rows={groupsQuery.data.items}
        getRowId={(group) => group.id}
        project={project}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(group) => {
          open(group.id);
        }}
        renderOverride={(field, group) => {
          if (field.name !== "roleIds") return undefined;
          return renderCell(
            field,
            group.roleIds.map((id) => roleNames.get(id) ?? id),
          );
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={groupsQuery.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
